import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ApiService, CoreUiPartialState, ElectronService, getSecurityFunctions, SpinnerService } from '@pos-app/core-ui';
import { KEYS } from '@pos-app/data';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, finalize, take, takeUntil } from 'rxjs/operators';
import { LoadParkedOrdersList } from '../orders/+state/orders.actions';
import { OrdersPartialState } from '../orders/+state/orders.reducer';
import { ResourceItem, RESOURCE_TYPE } from '../../core/models/resources.model';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'app-resources',
    templateUrl: './resources.component.html',
    styleUrls: ['./resources.component.scss'],
    standalone: false
})
export class ResourcesComponent implements OnInit, OnDestroy {
  categories: string[];
  listToShow: ResourceItem[] = [];
  selectedCategory = '';
  contentSearch: UntypedFormControl;
  allFiles: ResourceItem[] = [];
  allLinks: ResourceItem[] = [];
  page = 1;
  pageSize = 10;
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  private contentToShowSubject = new BehaviorSubject<ResourceItem[]>([]);
  contentToShow$ = this.contentToShowSubject.asObservable();
  unSubscribe$ = new Subject<void>();
  securityFunctions: string[];
  selectedCategoryContent: ResourceItem[];
  selectedDirectory: string = this.route.snapshot.params.id;
  columnSelected: string = 'lastModified';
  direction: 'asc' | 'desc' = 'asc';

  constructor(
    private apiService: ApiService,
    private store: Store<CoreUiPartialState>,
    private orderStore: Store<OrdersPartialState>,
    private electronService: ElectronService,
    private route: ActivatedRoute,
    public spinnerService: SpinnerService,
  ) {}

  ngOnInit() {
    this.contentSearch = new UntypedFormControl('');
    const catalogueStatus = JSON.parse(localStorage.getItem(KEYS.catalogueStatus));
    this.store
      .select(getSecurityFunctions)
      .pipe(take(1))
      .subscribe((res) => (this.securityFunctions = res));
    this.isLoadingSubject.next(true);
    // show global spinner as well (keeps reference counted state)
    try {
      this.spinnerService.show();
    } catch (e) {
      /* ignore if spinner service unavailable */
    }

    console.debug('[Resources] getResources start');

    this.apiService
      .getResources(catalogueStatus['customerCategory'], this.securityFunctions)
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
          // Debug: confirm finalize invoked
          // eslint-disable-next-line no-console
          console.debug('[Resources] getResources finalize -> isLoading=', this.isLoadingSubject.value);
          try {
            this.spinnerService.hide();
          } catch (e) {
            console.warn('[Resources] spinnerService.hide() failed', e);
          }
        })
      )
      .subscribe(
        (res) => {
          this.isLoadingSubject.next(false);
          // Debug: successful response
          // eslint-disable-next-line no-console
          console.debug('[Resources] getResources complete');
          if (res) {
          this.allFiles = this.getAllFiles(res, RESOURCE_TYPE.file).sort((a, b) => {
            return a.name > b.name ? 1 : -1;
          });
          this.allLinks = this.getAllFiles(res, RESOURCE_TYPE.link);
          this.categories = ([...new Set(res.map((item) => item.directory))] as string[]).filter((category) => category !== 'Related Websites');

          this.contentToShowSubject.next(this.allFiles);
          this.sortBy('lastModified');
          if (this.selectedDirectory) {
            this.selectedCategory = this.selectedDirectory;
            this.onSelectCategory();
          }
        }
        },
        (err) => {
          // Log unexpected errors
          // eslint-disable-next-line no-console
          console.error('[Resources] getResources error', err);
        }
      );

    this.contentSearch.valueChanges.pipe(takeUntil(this.unSubscribe$), debounceTime(300)).subscribe((value) => {
      this.search(value);
    });

    this.dispatchParkedOrdersList();
  }

  ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  extractFile(resourceItem: ResourceItem, allFiles: ResourceItem[], directory: string, type: string) {
    if (resourceItem.type === type) {
      allFiles.push({ ...resourceItem, directory });
    }
    if (resourceItem.type === RESOURCE_TYPE.folder && resourceItem.children) {
      resourceItem.children.forEach((child) => this.extractFile(child, allFiles, directory, type));
    }
  }

  getAllFiles(resources: ResourceItem[], type: string) {
    let allFiles = [];
    resources.forEach((item) => {
      this.extractFile(item, allFiles, item.directory, type);
    });
    return allFiles;
  }

  search(value: string) {
    this.page = 1;
    this.contentToShowSubject.next([]);
    value
      ? setTimeout(() => {
          const filtered = (this.selectedCategoryContent || this.allFiles).filter(
            (file) => file.name.toLowerCase().indexOf(value.toLowerCase()) !== -1,
          );
          this.contentToShowSubject.next(filtered);
        }, 0)
      : this.onSelectCategory();
  }

  hideRow(i) {
    // In pagination
    const isInPage = i >= (this.page - 1) * this.pageSize && i < (this.page - 1) * this.pageSize + this.pageSize;
    return !isInPage;
  }

  onSelectCategory() {
    this.contentSearch.patchValue('', { emitEvent: false });
    this.contentToShowSubject.next([]);
    setTimeout(() => {
      this.selectedCategoryContent = this.selectedCategory ? this.allFiles.filter((item) => item.directory === this.selectedCategory) : this.allFiles;
      this.contentToShowSubject.next(this.selectedCategoryContent);
      this.page = 1;
      this.columnSelected = 'lastModified';
      this.sortBy('lastModified', true);
    }, 0);
  }

  openLink(id: string, isOpeningAsUrl = false) {
    return this.apiService
      .getDownloadUrl(id, isOpeningAsUrl)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((res) => {
        if (this.electronService.isElectron) {
          if (isOpeningAsUrl) {
            this.electronService.openLinkNatively(res.fileUrl);
          } else {
            this.electronService.openFileNatively(res.fileUrl);
          }
          return;
        }
        window.open(res.fileUrl, isOpeningAsUrl ? '_blank' : '_self');
      });
  }

  dispatchParkedOrdersList() {
    this.orderStore.dispatch(new LoadParkedOrdersList());
  }
  sortBy(column: string, isInitialSort = false) {
    const current = this.contentToShowSubject.value || [];
    if (current.length === 0) {
      return;
    }
    if (this.columnSelected === column && !isInitialSort) {
      this.direction = this.direction === 'asc' ? 'desc' : 'asc';
    } else if (isInitialSort) {
      this.direction = 'desc';
    } else {
      this.direction = 'asc';
    }
    this.columnSelected = column;

    const sorted = [...current].sort((a, b) => {
      if (a[column] < b[column]) {
        return this.direction === 'asc' ? -1 : 1;
      }
      if (a[column] > b[column]) {
        return this.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    this.contentToShowSubject.next(sorted);
  }
}
